import { Box, Stack, TextField, Typography } from "@mui/material";

import { fieldSx } from "./sectionStyles";

import { type AudienceReachMedium } from "@/api/audiences";
import { AudienceReachBanner } from "@/features/BroadcastV2/components/AudienceReachBanner";
import { EnforceFrequencyCapSection } from "@/features/BroadcastV2/components/EnforceFrequencyCapSection";
import {
  SelectAudienceField,
  type SelectAudienceOption,
} from "@/features/BroadcastV2/components/SelectAudienceField";
import { SmartAudienceSection } from "@/features/BroadcastV2/components/SmartAudienceSection";
import {
  BROADCAST_NAME_MAX_LENGTH,
  DEFAULT_REACH_OPTIMIZATION_PERCENTAGE,
} from "@/features/BroadcastV2/types";

export interface BroadcastSendDetailsSectionProps {
  siteId: string;
  /** Broadcast channel; gates the reach estimate to channel-eligible profiles. */
  medium: AudienceReachMedium;
  name: string;
  onNameChange: (value: string) => void;
  nameError?: string;
  audienceOptions: SelectAudienceOption[];
  onAudiencesChange: (options: SelectAudienceOption[]) => void;
  audienceError?: string;
  /** Excluded ("do not send to") audiences. */
  excludedAudienceOptions?: SelectAudienceOption[];
  onExcludedAudiencesChange?: (options: SelectAudienceOption[]) => void;
  disabled?: boolean;
  reachOptimization?: boolean;
  onReachOptimizationChange?: (value: boolean) => void;
  reachOptimizationPercentage?: number;
  onReachOptimizationPercentageChange?: (value: number) => void;
  showEnforceFrequencyCap?: boolean;
  enforceFrequencyCap?: boolean;
  onEnforceFrequencyCapChange?: (value: boolean) => void;
}

/** Name + audience selection. Controlled; shared by email and SMS compose/edit. */
export function BroadcastSendDetailsSection({
  siteId,
  medium,
  name,
  onNameChange,
  nameError,
  audienceOptions,
  onAudiencesChange,
  audienceError,
  excludedAudienceOptions,
  onExcludedAudiencesChange,
  disabled,
  reachOptimization = false,
  onReachOptimizationChange,
  reachOptimizationPercentage = DEFAULT_REACH_OPTIMIZATION_PERCENTAGE,
  onReachOptimizationPercentageChange,
  showEnforceFrequencyCap = false,
  enforceFrequencyCap = false,
  onEnforceFrequencyCapChange,
}: BroadcastSendDetailsSectionProps) {
  const excludedOptions = excludedAudienceOptions ?? [];
  const targetIds = audienceOptions.map((option) => option.id);
  const excludedIds = excludedOptions.map((option) => option.id);
  return (
    <>
      <Typography variant="subtitle1">Send details</Typography>
      <Stack
        gap={4}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.background["paper-elevation-1"],
          borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
          p: 6,
        }}
      >
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Add a name for your broadcast
            </Typography>
            <Typography variant="body3" color="text.secondary">
              {name.length}/{BROADCAST_NAME_MAX_LENGTH}
            </Typography>
          </Stack>
          <TextField
            value={name}
            onChange={(e) =>
              onNameChange(e.target.value.slice(0, BROADCAST_NAME_MAX_LENGTH))
            }
            placeholder="Enter title"
            disabled={disabled}
            inputProps={{ maxLength: BROADCAST_NAME_MAX_LENGTH }}
            sx={(theme) => ({ width: "100%", ...fieldSx(theme) })}
            error={Boolean(nameError)}
            helperText={nameError || undefined}
          />
        </Stack>

        <Stack gap={1}>
          <Typography variant="body2" color="text.secondary">
            Select audiences
          </Typography>
          <Box
            sx={{
              borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
              backgroundColor: (theme) =>
                theme.palette.background["paper-elevation-1"],
            }}
          >
            <SelectAudienceField
              siteId={siteId}
              value={audienceOptions}
              onChange={onAudiencesChange}
              disabled={disabled}
              unavailableIds={excludedIds}
            />
          </Box>
          {audienceError && (
            <Typography variant="caption" color="error">
              {audienceError}
            </Typography>
          )}

          {onExcludedAudiencesChange && (
            <Stack gap={1} sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Do not send to
              </Typography>
              <Box
                sx={{
                  borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
                  backgroundColor: (theme) =>
                    theme.palette.background["paper-elevation-1"],
                }}
              >
                <SelectAudienceField
                  siteId={siteId}
                  value={excludedOptions}
                  onChange={onExcludedAudiencesChange}
                  disabled={disabled}
                  unavailableIds={targetIds}
                  ariaLabel="Do not send to audiences"
                  placeholder="e.g. Recent Purchasers, Unsubscribed..."
                />
              </Box>
            </Stack>
          )}

          <Stack gap={3} sx={{ mt: 3 }}>
            <AudienceReachBanner
              siteId={siteId}
              audienceIds={targetIds}
              reachOptimization={reachOptimization}
              reachOptimizationPercentage={reachOptimizationPercentage}
              excludedAudienceIds={excludedIds}
              medium={medium}
            />
            {onReachOptimizationChange &&
              onReachOptimizationPercentageChange && (
                <SmartAudienceSection
                  reachOptimization={reachOptimization}
                  onReachOptimizationChange={onReachOptimizationChange}
                  reachOptimizationPercentage={reachOptimizationPercentage}
                  onReachOptimizationPercentageChange={
                    onReachOptimizationPercentageChange
                  }
                  disabled={disabled}
                />
              )}
            {showEnforceFrequencyCap && onEnforceFrequencyCapChange && (
              <EnforceFrequencyCapSection
                medium={medium}
                enforceFrequencyCap={enforceFrequencyCap}
                onEnforceFrequencyCapChange={onEnforceFrequencyCapChange}
                disabled={disabled}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
